import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Square, Save, Download } from 'lucide-react';
import { useLocationTracking } from '../../hooks/useLocationTracking';
import { logError, logInfo } from '@utils/logger';
import { useSurvey } from '../../../../contexts/SurveyContext';
import { Button } from '../../../../components/common/Button';
import { ProfileVisualization } from './ProfileVisualization';
import { calculateGrade, detectCurves, smoothProfile } from '../../utils/profileAnalysis';
import { analyzeClearance } from '../../utils/clearanceAnalysis';
import { ConvoySpecsForm } from './ConvoySpecsForm';
import { ProfileMap } from './ProfileMap';
import { Toast } from '../../../../components/common/Toast';
import { RecordingStatus } from './RecordingStatus';
import { doc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../../config/firebase';

const PROFILE_COLLECTION = 'road_profiles';

export const RoadProfilePage = () => {
  const navigate = useNavigate();
  const { userLocation, hasSignal } = useLocationTracking();
  const { activeSurvey } = useSurvey();

  // Redirect if no active survey or not recording
  useEffect(() => {
    if (!activeSurvey) {
      setToast({
        type: 'error',
        message: 'Please load a survey first'
      });
      navigate('/roadscope');
      return;
    }
  }, [activeSurvey, navigate]);
  const [savedProfiles, setSavedProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [profilePoints, setProfilePoints] = useState([]);
  const [smoothedPoints, setSmoothedPoints] = useState([]);
  const [currentGrade, setCurrentGrade] = useState(null);
  const [curves, setCurves] = useState([]);
  const [annotations, setAnnotations] = useState([]);
  const [toast, setToast] = useState(null);
  const [recordingInterval, setRecordingInterval] = useState(null);
  const [recordingPrecision, setRecordingPrecision] = useState(10);
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [convoySpecs, setConvoySpecs] = useState({
    length: 30,
    clearance: 0.45,
    rearHeight: 0.45, // Add rear height with default same as clearance
    axleSpacing: 3.5,
    rearContact: 2.5,
    speed: 40,
    suspension: 'air'
  });
  const [clearanceIssues, setClearanceIssues] = useState([]);
  const [showConvoySettings, setShowConvoySettings] = useState(false);

  // Load saved profiles for current survey
  useEffect(() => {
    const loadProfiles = async () => {
      if (!activeSurvey?.id) return;
      
      try {
        const profilesRef = collection(db, PROFILE_COLLECTION);
        const q = query(profilesRef, where('surveyId', '==', activeSurvey.id));
        const snapshot = await getDocs(q);
        
        const profiles = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setSavedProfiles(profiles);
      } catch (error) {
        logError('Failed to load profiles:', error);
        setToast({
          type: 'error',
          message: 'Failed to load saved profiles'
        });
      }
    };

    loadProfiles();
  }, [activeSurvey]);

  // Clean up recording interval on unmount
  useEffect(() => {
    return () => {
      if (recordingInterval) {
        clearInterval(recordingInterval);
      }
    };
  }, [recordingInterval]);

  // Prevent recording if no survey is active
  useEffect(() => {
    if (!activeSurvey && isRecording) {
      stopRecording();
      setToast({
        type: 'error',
        message: 'Recording stopped - no active survey'
      });
    }
  }, [activeSurvey, isRecording]);

  const startRecording = () => {
    if (!activeSurvey) {
      setToast({
        type: 'error',
        message: 'Please load a survey before recording a profile'
      });
      return;
    }

    if (!hasSignal) {
      setToast({
        type: 'error',
        message: 'GPS signal required to start recording'
      });
      return;
    }

    setIsRecording(true);
    const interval = setInterval(() => {
      if (userLocation) {
        setProfilePoints(prevPoints => {
          // Check if we should record a new point based on precision setting
          const lastPoint = prevPoints[prevPoints.length - 1];
          if (!lastPoint || calculateDistanceToLastPoint(lastPoint, userLocation) >= recordingPrecision) {
            const newPoint = {
              latitude: userLocation.lat,
              longitude: userLocation.lng,
              elevation: userLocation.altitude || 0,
              timestamp: new Date().toISOString(),
              distance: calculateDistance(prevPoints),
              precision: recordingPrecision // Store precision setting with point
            };
            
            // Calculate grade if we have previous points
            if (prevPoints.length > 0) {
              const grade = calculateGrade(prevPoints[prevPoints.length - 1], {
                latitude: userLocation.lat,
                longitude: userLocation.lng,
                elevation: userLocation.altitude || 0
              });
              setCurrentGrade(grade);
            }
            
            return [...prevPoints, newPoint];
          }
          return prevPoints;
        });
      }
    }, 1000); // Check position every second but only record based on precision
    setRecordingInterval(interval);
    logInfo('Road profile recording started');
  };

  const stopRecording = () => {
    if (recordingInterval) {
      clearInterval(recordingInterval);
      setRecordingInterval(null);
    }
    setIsRecording(false);
    logInfo('Road profile recording stopped');
  };

  const calculateDistance = (points) => {
    if (points.length === 0) return 0;
    // Calculate cumulative distance from start
    const lastPoint = points[points.length - 1];
    if (!lastPoint) return 0;
    
    return lastPoint.distance + (
      points.length > 1 
        ? getDistanceBetweenPoints(
            lastPoint,
            { lat: userLocation.lat, lng: userLocation.lng }
          )
        : 0
    );
  };

  const getDistanceBetweenPoints = (p1, p2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = p1.latitude * Math.PI/180;
    const φ2 = p2.lat * Math.PI/180;
    const Δφ = (p2.lat - p1.latitude) * Math.PI/180;
    const Δλ = (p2.lng - p1.longitude) * Math.PI/180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c;
  };

  const calculateDistanceToLastPoint = (lastPoint, currentLocation) => {
    if (!lastPoint || !currentLocation) return Infinity;
    
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lastPoint.latitude * Math.PI/180;
    const φ2 = currentLocation.lat * Math.PI/180;
    const Δφ = (currentLocation.lat - lastPoint.latitude) * Math.PI/180;
    const Δλ = (currentLocation.lng - lastPoint.longitude) * Math.PI/180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c; // Returns distance in meters
  };

  const handleSave = async () => {
    try {
      if (!activeSurvey) {
        throw new Error('No active survey');
      }

      // Calculate profile characteristics
      const smoothed = smoothProfile(profilePoints);
      setSmoothedPoints(smoothed);
      
      const detectedCurves = detectCurves(smoothed, 0.1, {
        length: convoySpecs.length,
        width: convoySpecs.width || 2.6 // Default width if not specified
      });
      setCurves(detectedCurves);
      
      // Analyze clearance issues
      const issues = analyzeClearance(smoothed, convoySpecs);
      setClearanceIssues(issues);

      const profileData = {
        points: profilePoints,
        smoothedPoints: smoothed,
        characteristics: {
          totalDistance: profilePoints[profilePoints.length - 1]?.distance || 0,
          maxElevation: Math.max(...profilePoints.map(p => p.elevation)),
          minElevation: Math.min(...profilePoints.map(p => p.elevation)),
          maxSlope: calculateMaxSlope(profilePoints),
          minTurningRadius: Math.pow(convoySpecs.length, 2) / (2 * (convoySpecs.width || 2.6)),
          curves: detectedCurves,
          annotations: annotations,
          clearanceIssues: issues,
          convoySpecs,
          precision: recordingPrecision
        },
        createdAt: new Date().toISOString(),
        surveyId: activeSurvey?.id || null
      };

      // Save to Firestore
      const profileRef = doc(collection(db, PROFILE_COLLECTION));
      await setDoc(profileRef, profileData);
      
      // Create POI for this profile
      const startPoint = profilePoints[0];
      const poiRef = doc(collection(db, 'pois'));
      await setDoc(poiRef, {
        type: 'Profile',
        coordinates: {
          latitude: startPoint.latitude,
          longitude: startPoint.longitude
        },
        profileId: profileRef.id,
        surveyId: activeSurvey.id,
        notes: `Road profile recorded on ${new Date().toLocaleDateString()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      setToast({
        type: 'success',
        message: 'Road profile saved successfully'
      });

      // If there's an active survey, prompt to attach
      if (activeSurvey) {
        setShowAttachModal(true);
      }

      logInfo('Road profile saved successfully');
    } catch (error) {
      logError('Failed to save road profile:', error);
      setToast({
        type: 'error',
        message: 'Failed to save road profile'
      });
    }
  };

  const calculateMaxSlope = (points) => {
    if (points.length < 2) return 0;
    let maxSlope = 0;
    
    for (let i = 1; i < points.length; i++) {
      const elevationChange = points[i].elevation - points[i-1].elevation;
      const distance = points[i].distance - points[i-1].distance;
      const slope = Math.abs((elevationChange / distance) * 100);
      maxSlope = Math.max(maxSlope, slope);
    }
    
    return maxSlope;
  };

  const detectCurves = (points) => {
    // TODO: Implement curve detection algorithm
    return [];
  };

  return (
    <div className="min-h-screen bg-[#3D3F42]">
      {/* Header */}
      <div className="sticky top-0 bg-[#2A2B2E] shadow-sm z-[50] p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-700 rounded-lg"
          >
            <ArrowLeft className="w-6 h-6 text-white" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-white">Road Profile</h1>
            <p className="text-sm text-gray-400">Record elevation profiles and road characteristics</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4 max-w-7xl mx-auto space-y-4">
        {/* Recording Settings */}
        <div className="bg-[#2A2B2E] rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-medium text-white">Precision</h2>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={recordingPrecision}
                  onChange={(e) => setRecordingPrecision(Number(e.target.value))}
                  className="w-32"
                  disabled={isRecording}
                />
                <span className="text-sm text-gray-400 min-w-[3rem]">{recordingPrecision}m</span>
              </div>
            </div>
            <div className="text-xs text-gray-500">Points recorded every {recordingPrecision}m</div>
          </div>
        </div>

        {/* Convoy Settings */}
        <div className="bg-[#2A2B2E] rounded-lg p-3">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-white">Convoy Specifications</h2>
            <Button
              onClick={() => setShowConvoySettings(!showConvoySettings)}
              className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-3 py-1"
            >
              {showConvoySettings ? 'Hide Settings' : 'Show Settings'}
            </Button>
          </div>
          {showConvoySettings && (
            <ConvoySpecsForm
              specs={convoySpecs}
              onChange={setConvoySpecs}
              disabled={isRecording}
            />
          )}
        </div>

        {/* Profile Selection */}
        {savedProfiles.length > 0 && !isRecording && (
          <div className="bg-[#2A2B2E] rounded-lg p-4">
            <h2 className="text-lg font-medium text-white mb-4">Saved Profiles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedProfiles.map(profile => (
                <div 
                  key={profile.id}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                    selectedProfile?.id === profile.id 
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-gray-600 hover:border-blue-400 bg-gray-700/50'
                  }`}
                  onClick={() => setSelectedProfile(profile)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-white">
                        Profile #{profile.id.slice(-6)}
                      </p>
                      <p className="text-sm text-gray-400">
                        {new Date(profile.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-sm text-gray-400">
                      {(profile.characteristics.totalDistance / 1000).toFixed(2)} km
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Map View */}
          <div className="bg-[#2A2B2E] rounded-lg p-4">
            <h2 className="text-lg font-medium text-white mb-4">Plan View</h2>
            <ProfileMap 
              points={selectedProfile?.points || profilePoints}
              currentLocation={userLocation}
              isRecording={isRecording}
            />
          </div>

          {/* Profile View */}
          <div className="bg-[#2A2B2E] rounded-lg p-4">
            <h2 className="text-lg font-medium text-white mb-4">Elevation Profile</h2>
            <ProfileVisualization 
              points={selectedProfile?.points || profilePoints}
              smoothedPoints={selectedProfile?.smoothedPoints || smoothedPoints}
              curves={selectedProfile?.characteristics?.curves || curves}
              annotations={selectedProfile?.characteristics?.annotations || annotations}
              clearanceIssues={selectedProfile?.characteristics?.clearanceIssues || clearanceIssues}
              onAnnotationAdd={selectedProfile ? undefined : (annotation) => setAnnotations([...annotations, annotation])}
              onAnnotationUpdate={selectedProfile ? undefined : (index, annotation) => {
                const newAnnotations = [...annotations];
                newAnnotations[index] = annotation;
                setAnnotations(newAnnotations);
              }}
              onAnnotationDelete={selectedProfile ? undefined : (index) => {
                setAnnotations(annotations.filter((_, i) => i !== index));
              }}
              isRecording={isRecording}
            />
          </div>
        </div>
        
        {/* Clearance Issues */}
        {clearanceIssues.length > 0 && (
          <div className="bg-[#2A2B2E] rounded-lg p-4">
            <h2 className="text-lg font-medium text-white mb-4">Clearance Issues</h2>
            <div className="space-y-2">
              {clearanceIssues.map((issue, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-lg ${
                    issue.severity === 'critical' 
                      ? 'bg-red-500/10 text-red-400'
                      : 'bg-yellow-500/10 text-yellow-400'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{issue.message}</p>
                      <p className="text-sm opacity-80">
                        At {(issue.distance / 1000).toFixed(2)}km
                      </p>
                    </div>
                    <div className="text-sm">
                      K-factor: {issue.kFactor.toFixed(1)}
                      <br />
                      Required: {issue.minRequired.toFixed(1)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-[#2A2B2E] px-6 py-3 rounded-full shadow-lg">
        <Button
          onClick={isRecording ? stopRecording : startRecording}
          className={`flex items-center gap-2 ${
            isRecording 
              ? 'bg-red-600 hover:bg-red-700' 
              : 'bg-green-600 hover:bg-green-700'
          } text-white px-6`}
          disabled={!hasSignal}
        >
          {isRecording ? (
            <>
              <Square className="w-4 h-4" />
              Stop Recording
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              Start Recording
            </>
          )}
        </Button>

        {profilePoints.length > 0 && (
          <>
            <Button
              onClick={handleSave}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={isRecording}
            >
              <Save className="w-4 h-4 mr-2" />
              Save Profile
            </Button>
            <Button
              onClick={() => {
                // TODO: Implement export to DXF
              }}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              disabled={isRecording}
            >
              <Download className="w-4 h-4 mr-2" />
              Export DXF
            </Button>
          </>
        )}
      </div>

      {/* Recording Status */}
      {isRecording && (
        <RecordingStatus
          pointCount={profilePoints.length}
          lastPoint={profilePoints[profilePoints.length - 1]}
          currentGrade={currentGrade}
          currentLocation={userLocation}
        />
      )}

      {/* Toast Notifications */}
      <div className="pb-32"></div> {/* Add bottom padding for scrolling */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default RoadProfilePage;